import { useEffect, useState } from 'react';
import type { MarkerPoint } from '@tiw/globe';

/**
 * Load a static GeoJSON of Point features (served from apps/web/public/data)
 * into a flat list of {lat, lon} for the Markers renderer. Used for the
 * cloud-region substrate. Fetched once per url; empty until loaded.
 */
interface PointGeoJSON {
  readonly features?: ReadonlyArray<{
    readonly geometry?: { readonly type?: string; readonly coordinates?: number[] };
  }>;
}

export function usePoints(url: string): readonly MarkerPoint[] {
  const [points, setPoints] = useState<readonly MarkerPoint[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<PointGeoJSON>) : Promise.reject(new Error(String(r.status)))))
      .then((geo) => {
        if (cancelled) return;
        const out: MarkerPoint[] = [];
        for (const feature of geo.features ?? []) {
          const geom = feature.geometry;
          if (geom?.type !== 'Point' || !geom.coordinates) continue;
          const lon = geom.coordinates[0];
          const lat = geom.coordinates[1];
          if (typeof lon === 'number' && typeof lat === 'number') out.push({ lat, lon });
        }
        setPoints(out);
      })
      .catch(() => {
        if (!cancelled) setPoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return points;
}
